import { useEffect, useState } from 'react';
import { ScrollText, Save } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '../ui';

type Mention = { cle: string; libelle: string; texte: string; actif: boolean; ordre: number };

/** Paramètres > Documents > Mentions légales : textes ajoutés au pied des devis et factures. */
export function MentionsDocumentsEditor() {
  const [rows, setRows] = useState<Mention[]>([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    supabase.from('mentions_documents').select('*').order('ordre').then(({ data }) => setRows((data as any) || []));
  }, []);
  const upd = (cle: string, p: Partial<Mention>) => setRows((r) => r.map((m) => (m.cle === cle ? { ...m, ...p } : m)));
  const save = async () => {
    setSaving(true);
    for (const m of rows) {
      const { error } = await supabase.from('mentions_documents').update({ texte: m.texte, actif: m.actif, updated_at: new Date().toISOString() }).eq('cle', m.cle);
      if (error) { setSaving(false); toast.error(`Enregistrement impossible : ${error.message}`); return; }
    }
    setSaving(false);
    toast.success('Mentions enregistrées — elles s’appliquent aux nouvelles factures.');
  };
  return (
    <Card className="p-5 mb-6 border-amber-500/30">
      <div className="flex items-start gap-3 mb-4">
        <ScrollText className="text-amber-400 shrink-0" size={20} strokeWidth={1.5} />
        <div>
          <h3 className="text-base font-semibold text-slate-100 mb-1">Documents — Mentions légales</h3>
          <p className="text-xs text-slate-400">Textes ajoutés automatiquement en bas des nouvelles factures. Décochez une mention pour ne plus l’afficher.</p>
        </div>
      </div>
      <div className="space-y-4">
        {rows.map((m) => (
          <div key={m.cle}>
            <label className="flex items-center gap-2 text-sm text-slate-200 mb-1">
              <input type="checkbox" checked={m.actif} onChange={(e) => upd(m.cle, { actif: e.target.checked })} /> {m.libelle}
            </label>
            <textarea value={m.texte} onChange={(e) => upd(m.cle, { texte: e.target.value })} rows={2}
              className="w-full px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-md text-sm text-slate-200 focus:outline-none focus:border-amber-500/50" />
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} className="mt-4 flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg text-sm font-medium disabled:opacity-50">
        <Save size={16} strokeWidth={1.5} /> Enregistrer les mentions
      </button>
    </Card>
  );
}

/** Texte combiné des mentions actives (pied de facture). */
export async function fetchMentionsActives(): Promise<string> {
  const { data } = await supabase.from('mentions_documents').select('texte, actif, ordre').eq('actif', true).order('ordre');
  return ((data as any[]) || []).map((m) => String(m.texte || '').trim()).filter(Boolean).join(' ');
}
